/**
 * Session RPC Procedures (CLI-facing).
 *
 * The session-scoped surface the heysegment CLI talks to. Every procedure
 * authorizes via `RpcSessionMiddleware` (the session id + token), so the CLI can
 * build a client from THIS group alone and stay unaware of the rest of the API.
 *
 *   - getPullUrl         -> presigned GET for the session's source bundle (sets base revision)
 *   - getPushUrl         -> presigned PUT for the session's version bundle (gated on safe state)
 *   - report             -> append an origin-tagged event (the generalized capture point)
 *   - getSafetyState     -> the contention verdict (drift / concurrent sessions)
 *   - acknowledgeOverride-> accept an override for the current revision (unblocks push)
 *   - heartbeat          -> keep the session "active" for concurrent-edit detection
 *   - getSessionSiteDatabases -> the session workspace's database definitions for codegen
 *   - listRecall         -> catalog of recallable workspace knowledge (business, images, …)
 *   - getRecall          -> one recall topic rendered as markdown for the agent
 *
 * @module
 */
import { Schema as S } from "effect"
import { Rpc, RpcGroup } from "effect/unstable/rpc"
import { RpcSessionMiddleware } from "../middleware/session.js"
import { RpcDatabaseMiddleware } from "../middleware/database.js"
import { CodegenDatabaseSchema } from "../schemas/site-database-codegen.js"

// ============================================================================
// Schemas
// ============================================================================

export const PresignedUrlSchema = S.Struct({
  /** The presigned URL to GET (pull) or PUT (push). */
  url: S.String,
  /** The resolved R2 object key the URL targets (diagnostic). */
  key: S.String,
  /** Seconds until the URL expires. */
  expiresIn: S.Number,
})
export type PresignedUrl = typeof PresignedUrlSchema.Type

export const OkSchema = S.Struct({ ok: S.Literal(true) })
export type Ok = typeof OkSchema.Type

/** The taxonomy of reportable events (validated at the wire). */
export const SessionEventType = S.Literals([
  "session.started",
  "session.closed",
  "pulled",
  "pushed",
  "validated",
  "file.changed",
  "tool.used",
  "prompt.submitted",
  "deploy.triggered",
  "deploy.completed",
  "conflict.detected",
  "override.acknowledged",
  "codegen.generated",
  "image.uploaded",
  "video.uploaded",
])
export type SessionEventType = typeof SessionEventType.Type

export const ConcurrentSessionSchema = S.Struct({
  origin: S.String,
  lastSeenAt: S.String,
})

export const SafetyStateSchema = S.Struct({
  currentRevision: S.Number,
  baseRevision: S.Number,
  driftDetected: S.Boolean,
  concurrent: S.Boolean,
  contended: S.Boolean,
  acknowledged: S.Boolean,
  safeToPush: S.Boolean,
  reasons: S.Array(S.String),
  concurrentSessions: S.Array(ConcurrentSessionSchema),
})
export type SafetyState = typeof SafetyStateSchema.Type

/** The session workspace's database definitions, in codegen wire shape. */
export const SiteDatabasesSchema = S.Struct({
  databases: S.Array(CodegenDatabaseSchema),
})
export type SiteDatabases = typeof SiteDatabasesSchema.Type

/** One entry in the recall catalog (what knowledge is available + how populated). */
export const RecallTopicInfoSchema = S.Struct({
  key: S.String,
  title: S.String,
  summary: S.String,
  group: S.String,
  available: S.Boolean,
  hint: S.String,
})
export type RecallTopicInfo = typeof RecallTopicInfoSchema.Type

/** A single recall topic rendered to markdown for the agent. */
export const RecallDocSchema = S.Struct({
  key: S.String,
  title: S.String,
  markdown: S.String,
})
export type RecallDoc = typeof RecallDocSchema.Type

/** Lifecycle status of the version the session is bound to. */
export const VersionLifecycleStatus = S.Literals(["draft", "published", "live"])
export type VersionLifecycleStatus = typeof VersionLifecycleStatus.Type

/** Recommended next action for the CLI given the bound version's currency. */
export const VersionRecommendation = S.Literals(["ok", "pull", "switch"])
export type VersionRecommendation = typeof VersionRecommendation.Type

/**
 * Whether the session's bound version is still the workspace's working
 * (editable) version, and what the CLI should do about it.
 *
 *   - `ok`     — bound version is the working version and the source is current.
 *   - `pull`   — bound version is the working version but it advanced (revision drift).
 *   - `switch` — bound version is frozen/superseded; rebind to the working version.
 */
export const VersionStatusSchema = S.Struct({
  boundVersionId: S.String,
  boundVersionNumber: S.NullOr(S.Number),
  boundStatus: VersionLifecycleStatus,
  /** `made_live_at` is set — the version can no longer be edited. */
  boundFrozen: S.Boolean,
  /** The workspace's current editable version, or null if every version is frozen. */
  workingVersionId: S.NullOr(S.String),
  workingVersionNumber: S.NullOr(S.Number),
  liveVersionId: S.NullOr(S.String),
  isCurrent: S.Boolean,
  recommendation: VersionRecommendation,
})
export type VersionStatus = typeof VersionStatusSchema.Type

/**
 * Result of rebinding the session to the workspace's working version. When
 * `alreadyCurrent` is true the bound version was already the working version and
 * no new session was minted (the token fields echo the existing session).
 */
export const RebindResultSchema = S.Struct({
  alreadyCurrent: S.Boolean,
  sessionId: S.String,
  sessionToken: S.String,
  versionId: S.String,
  versionNumber: S.Number,
  /** True when the rebind forked a brand-new draft from the live version. */
  createdNew: S.Boolean,
})
export type RebindResult = typeof RebindResultSchema.Type

// ── Images (CLI upload) ─────────────────────────────────────────────────

/** Presigned PUT for a new workspace image. */
export const ImageUploadUrlSchema = S.Struct({
  /** The image id to register the upload with once the PUT completes. */
  imageId: S.String,
  /** The presigned URL to PUT the image bytes to. */
  url: S.String,
  /** Seconds until the URL expires. */
  expiresIn: S.Number,
})
export type ImageUploadUrl = typeof ImageUploadUrlSchema.Type

/** A workspace image in CLI wire shape, including its canonical public URL. */
export const CliImageSchema = S.Struct({
  id: S.String,
  type: S.String,
  /** `${MANAGED_MEDIA_URL}/media/{id}.{type}` — paste straight into `<img src>`. */
  url: S.String,
  title: S.NullOr(S.String),
  description: S.NullOr(S.String),
  fileSize: S.NullOr(S.Number),
  width: S.NullOr(S.Number),
  height: S.NullOr(S.Number),
})
export type CliImage = typeof CliImageSchema.Type

/**
 * Result of registering an upload — the stored image plus whether the server
 * dispatched AI title/description generation (it does so only when no title was
 * supplied on the wire).
 */
export const ConfirmedImageSchema = S.Struct({
  id: S.String,
  type: S.String,
  url: S.String,
  title: S.NullOr(S.String),
  description: S.NullOr(S.String),
  fileSize: S.NullOr(S.Number),
  width: S.NullOr(S.Number),
  height: S.NullOr(S.Number),
  /** True when the server kicked off async AI description (no title supplied). */
  analyzing: S.Boolean,
})
export type ConfirmedImage = typeof ConfirmedImageSchema.Type

// ── Videos (CLI upload) ─────────────────────────────────────────────────

/** Presigned PUT for a new workspace video. */
export const VideoUploadUrlSchema = S.Struct({
  /** The video id to register the upload with once the PUT completes. */
  videoId: S.String,
  /** The presigned URL to PUT the raw video bytes to. */
  url: S.String,
  /**
   * The file extension the server keyed R2 at (`media/{videoId}.{type}`).
   * Videos are stored as-is, so this is derived from the file name (never
   * re-encoded) — pass it back to `registerVideoUpload`.
   */
  type: S.String,
  /** Seconds until the URL expires. */
  expiresIn: S.Number,
})
export type VideoUploadUrl = typeof VideoUploadUrlSchema.Type

/** A workspace video in CLI wire shape, including its canonical public URL. */
export const CliVideoSchema = S.Struct({
  id: S.String,
  type: S.String,
  /** `${MANAGED_MEDIA_URL}/media/{id}.{type}` — reference in a `<video><source>`. */
  url: S.String,
  title: S.NullOr(S.String),
  description: S.NullOr(S.String),
  fileSize: S.NullOr(S.Number),
  width: S.NullOr(S.Number),
  height: S.NullOr(S.Number),
  durationSeconds: S.NullOr(S.Number),
})
export type CliVideo = typeof CliVideoSchema.Type

// ============================================================================
// Error
// ============================================================================

export class SessionError extends S.TaggedErrorClass<SessionError>()(
  "SessionError",
  { message: S.String },
) {}

// ============================================================================
// RPC Group
// ============================================================================

export const SessionRpc = RpcGroup.make(
  Rpc.make("getPullUrl", {
    success: PresignedUrlSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  Rpc.make("getPushUrl", {
    success: PresignedUrlSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  Rpc.make("report", {
    payload: {
      type: SessionEventType,
      data: S.optional(S.Record(S.String, S.Unknown)),
    },
    success: OkSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  Rpc.make("getSafetyState", {
    success: SafetyStateSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  Rpc.make("acknowledgeOverride", {
    success: SafetyStateSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  Rpc.make("heartbeat", {
    success: OkSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // Codegen source: the session workspace's database definitions. No payload —
  // `workspaceId` is resolved from the authenticated session (SessionContext),
  // never trusted from the wire.
  Rpc.make("getSessionSiteDatabases", {
    success: SiteDatabasesSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // Recall catalog: what business knowledge the agent can pull for this
  // workspace, and whether each topic is populated. Workspace from the session.
  Rpc.make("listRecall", {
    success: S.Struct({ topics: S.Array(RecallTopicInfoSchema) }),
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // One recall topic rendered as markdown for the agent to ingest.
  Rpc.make("getRecall", {
    payload: { topic: S.String },
    success: RecallDocSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // Read-only: is the bound version still the workspace's working version?
  Rpc.make("getVersionStatus", {
    success: VersionStatusSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // Rebind the session to the working version (forking from live if needed),
  // minting a fresh session bound to it. Authorized by the current session.
  Rpc.make("adoptWorkingVersion", {
    success: RebindResultSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // ── Images ──────────────────────────────────────────────────────────
  //
  // Distinct method names from the workspace-authed web `ImageRpc`: all RPC
  // groups merge into one served group, and merge is last-writer-wins by tag,
  // so a shared name would clobber the web handler. Workspace is resolved from
  // the session (SessionContext), never trusted from the wire.

  // Presign an R2 PUT for a new image in the session's workspace.
  Rpc.make("presignImageUpload", {
    payload: {
      /** Content-type of the bytes about to be PUT (post-optimize). */
      mimeType: S.String,
      /** Original file name — diagnostic only. */
      fileName: S.optional(S.String),
    },
    success: ImageUploadUrlSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // Register a completed upload as an images row. When `title` is supplied the
  // server writes it verbatim and skips AI description; when omitted it kicks
  // off the async describer (matching the web upload flow).
  Rpc.make("registerImageUpload", {
    payload: {
      imageId: S.String,
      width: S.optional(S.Number),
      height: S.optional(S.Number),
      title: S.optional(S.String.check(S.isMaxLength(200))),
      description: S.optional(S.String.check(S.isMaxLength(500))),
    },
    success: ConfirmedImageSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // List the session workspace's images for `heysegment images list`.
  Rpc.make("listImages", {
    success: S.Struct({ images: S.Array(CliImageSchema) }),
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // ── Videos ──────────────────────────────────────────────────────────
  //
  // Distinct method names from the workspace-authed web `VideoRpc`
  // (`getVideoUploadUrl` / `confirmVideoUpload` / `getVideos`): all RPC groups
  // merge into one served group, last-writer-wins by tag, so a shared name
  // would clobber the web handler. Workspace comes from the session, never the
  // wire. Videos are stored raw — no optimize, no AI description.

  // Presign an R2 PUT for a new video in the session's workspace. `fileName`
  // gives the authoritative extension (video isn't re-encoded); the resolved
  // extension is returned as `type` for the register call.
  Rpc.make("presignVideoUpload", {
    payload: {
      /** Content-type of the raw bytes about to be PUT. */
      mimeType: S.String,
      /** Original file name — the extension is authoritative for `type`. */
      fileName: S.String,
    },
    success: VideoUploadUrlSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // Register a completed upload as a videos row. `type` is the extension
  // returned by presignVideoUpload. title/description are stored verbatim and
  // left null when omitted — videos are never auto-described.
  Rpc.make("registerVideoUpload", {
    payload: {
      videoId: S.String,
      type: S.String,
      title: S.optional(S.String.check(S.isMaxLength(200))),
      description: S.optional(S.String.check(S.isMaxLength(500))),
      width: S.optional(S.Number),
      height: S.optional(S.Number),
      durationSeconds: S.optional(S.Number),
    },
    success: CliVideoSchema,
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),

  // List the session workspace's videos for `heysegment videos list`.
  Rpc.make("listVideos", {
    success: S.Struct({ videos: S.Array(CliVideoSchema) }),
    error: SessionError,
  })
    .middleware(RpcSessionMiddleware)
    .middleware(RpcDatabaseMiddleware),
)
